import { Box, Button, Card, CardContent, InputLabel, TextField, Typography } from '@mui/material';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { PlusIcon, TrashIcon } from '~/assets/icons';
import { BaseRow } from '~/components/common';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';

import EditInvoiceDialog from '~/components/invoices/edit-invoice-dialog/EditInvoiceDialog';
import InvoiceList from '~/components/invoices/InvoiceList/InvoiceList';
import usePermission from '~/hooks/usePermission';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { invoiceActions } from '~/thunks/invoice/invoiceSlice';
import { handleGetInvoice, handleGetInvoices } from '~/thunks/invoice/invoiceThunk';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT, FIXED_VALUE } from '~/utils/constants/common';
import { PERMISSIONS } from '~/utils/constants/permission';
import { cast, catchError, handleApiError, notify } from '~/utils/helper';
import { IInvoice, IPayloadDeleteInvoice } from '~/utils/interface/invoice';
import { IJob, IUpdateJobPayload } from '~/utils/interface/job';
import styles from './Financial.module.scss';
import classNames from 'classnames/bind';
import invoiceApi from '~/apis/invoice';
import COGSList from '~/components/invoices/cogs/COGSList';
import { ICogs } from '~/utils/interface/cogs';
import { handleGetCogs, handleGetCogsList } from '~/thunks/cogs/cogsThunk';
import { cogsActions } from '~/thunks/cogs/cogsSlice';
import cogsApi from '~/apis/cogs';
import EditCOGSDialog from '~/components/invoices/edit-cogs-dialog/EditCOGSDialog';
import { handleGetTimeClocksSelection } from '~/thunks/time-clocks/timeClocksThunk';
import { handleUpdateJob } from '~/thunks/job/jobThunk';
import { useFormik } from 'formik';

type FinancialProps = {
  data: IJob | undefined;
  updateData: () => void;
};

const cx = classNames.bind(styles);

const initialValues: IUpdateJobPayload = {
  job: {
    status: '',
    jobTypeId: '',
    statusId: '',
    testing: [],
  },
  jobAssigneeIds: [],
};
const Financial = (props: FinancialProps) => {
  //#region Destructuring Props
  //#endregion Destructuring Props
  const { data } = props;

  //#region Declare Hook
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/invoice']);
  const permissions = usePermission();
  //#endregion Declare Hook

  //#region Selector
  const { filter, needRefreshData, selectedIds, openAlertDelete, menuSelectedId } = useAppSelector(
    (state) => state.invoice
  );

  const { filterCogs, needRefreshDataCogs, selectedIdsCogs, openAlertDeleteCogs, menuSelectedIdCogs } = useAppSelector(
    (state) => state.cogs
  );

  //#endregion Selector

  //#region Declare State
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openEditDialogCOGS, setOpenEditDialogCOGS] = useState<boolean>(false);
  const [editDialogMode, setEditDialogMode] = useState<string>(DIALOG_MODE_CREATE);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<IInvoice>();
  const [editDataCogs, setEditDataCogs] = useState<ICogs>();

  //#endregion Declare State
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      try {
        await dispatch(
          handleUpdateJob({
            id: Number(data?.id),
            params: { ...values, jobAssigneeIds: values?.jobAssigneeIds?.length ? values.jobAssigneeIds : '' },
          })
        ).unwrap();
        notify('success', t('editSuccess', { ns: 'admin/common' }));
        setIsEditing(false);
        if (props.updateData) props.updateData();
      } catch (e: unknown) {
        handleApiError(e, setFieldError);
      }
    },
  });
  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (props.updateData) props.updateData();
    setIsEditing(false);
  };
  //#region Implement Hook

  useEffect(() => {
    form.setFieldValue('job.status', cast(data?.status));
    form.setFieldValue('job.statusId', cast(data?.statusId));
    form.setFieldValue('jobAssigneeIds', cast(data?.assigneeIds));
    form.setFieldValue('job.estimatedCosts', cast(data?.estimatedCosts));

    form.setFieldValue('job.estimatedRevenue', cast(data?.estimatedRevenue));

    form.setFieldValue('job.accrualRevenue', cast(data?.accrualRevenue));
    form.setFieldValue('job.cashRevenue', cast(data?.cashRevenue));

    form.setFieldValue('job.costs', cast(data?.costs));

    form.setFieldValue('job.jobTypeId', cast(data?.jobTypeId));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isEditing]);

  useEffect(() => {
    dispatch(handleGetInvoices({ ...filter, jobId: String(props?.data?.id) }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filter, needRefreshData]);

  useEffect(() => {
    dispatch(handleGetTimeClocksSelection({ jobId: String(props?.data?.id) })).unwrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, needRefreshData]);

  useEffect(() => {
    dispatch(handleGetCogsList({ ...filterCogs, jobId: String(props?.data?.id) }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filterCogs, needRefreshDataCogs]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleOpenEditInvoice = (isOpen: boolean, needFreshData: boolean = false) => {
    setEditDialogMode(DIALOG_MODE_CREATE);
    setOpenEditDialog(isOpen);
    setEditData(undefined);
    if (needFreshData) {
      dispatch(invoiceActions.changeNeedRefreshData(true));
    }
  };

  const handleOpenEditCOGS = (isOpen: boolean, needFreshData: boolean = false) => {
    setEditDialogMode(DIALOG_MODE_CREATE);
    setOpenEditDialogCOGS(isOpen);
    setEditDataCogs(undefined);
    if (needFreshData) {
      dispatch(cogsActions.changeNeedRefreshData(true));
      dispatch(invoiceActions.changeNeedRefreshData(true));
    }
  };

  const handleOpenEdit = async (id: string) => {
    try {
      const response = await dispatch(handleGetInvoice(id)).unwrap();
      setEditData(response);
      setEditDialogMode(DIALOG_MODE_EDIT);
      setOpenEditDialog(true);
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleOpenEditDataCOGS = async (id: string) => {
    try {
      const response = await dispatch(handleGetCogs(id)).unwrap();
      setEditDataCogs(response);
      setEditDialogMode(DIALOG_MODE_EDIT);
      setOpenEditDialogCOGS(true);
    } catch (e) {
      handleApiError(e);
    }
  };

  const openAlertDeleteModal = useCallback(() => {
    dispatch(invoiceActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const openAlertDeleteModalCOGS = useCallback(() => {
    dispatch(cogsActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(invoiceActions.changeMenuSelectId(undefined));
    dispatch(invoiceActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const closeAlertDeleteModalCOGS = useCallback(() => {
    dispatch(cogsActions.changeMenuSelectId(undefined));
    dispatch(cogsActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const onDeleteSelectedJobs = useCallback(async () => {
    let payloadDelete: IPayloadDeleteInvoice =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await invoiceApi.delete(payloadDelete);
      notify('success', t('deleteSuccessMessage', { ns: 'admin/common' }));
      dispatch(invoiceActions.changeNeedRefreshData(true));
      dispatch(invoiceActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, menuSelectedId, selectedIds, t]);

  const onDeleteSelectedCOGS = useCallback(async () => {
    let payloadDelete: IPayloadDeleteInvoice =
      menuSelectedIdCogs !== undefined ? { ids: [menuSelectedIdCogs] } : { ids: [...selectedIdsCogs] };
    try {
      await cogsApi.delete(payloadDelete);
      notify('success', t('deleteSuccessMessage', { ns: 'admin/common' }));
      dispatch(cogsActions.changeNeedRefreshData(true));
      dispatch(cogsActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, menuSelectedIdCogs, selectedIdsCogs, t]);

  //#endregion Handle Function

  return (
    <>
      <Card className='mb-[30px]'>
        <CardContent>
          <BaseRow className='mb-[30px]'>
            <Typography variant='h5'>{t('financialTitle')}</Typography>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[30px]'>
            <Box className='w-full'>
              <InputLabel>{t('financial.estimatedRevenue')}</InputLabel>
              <NumericFormat
                customInput={TextField}
                value={cast(form.values.job.estimatedRevenue)}
                onValueChange={(val) => {
                  form.setFieldValue(`job.estimatedRevenue`, val.floatValue);
                  form.setFieldValue(
                    `job.estimatedGrossProfit`,
                    (val?.floatValue ?? 0) - (form.values.job?.estimatedCosts ?? 0)
                  );

                  const estimatedGrossProfit = (val?.floatValue ?? 0) - (form.values.job?.estimatedCosts ?? 0);
                  const estimatedRevenue = val?.floatValue ?? 1;
                  const estimatedMargin = parseFloat(
                    ((estimatedGrossProfit / estimatedRevenue) * 100).toFixed(FIXED_VALUE)
                  );

                  const accrualRevenue = form.values.job?.accrualRevenue;
                  const projectBilled =
                    accrualRevenue && estimatedRevenue
                      ? ((accrualRevenue / estimatedRevenue) * 100).toFixed(FIXED_VALUE)
                      : 0;
                  form.setFieldValue(`job.projectBilled`, projectBilled);
                  form.setFieldValue(`job.estimatedMargin`, estimatedMargin);
                }}
                thousandSeparator={true}
                prefix={'$'}
                disabled={!isEditing}
                decimalScale={2}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('financial.accrualRevenue')}</InputLabel>
              <NumericFormat
                customInput={TextField}
                value={cast(form.values.job.accrualRevenue)}
                onValueChange={(val) => {
                  form.setFieldValue(`job.accrualRevenue`, val.floatValue);
                  // Update projectBilled
                  const accrualRevenue = val.floatValue ?? 0;
                  const estimatedRevenue = form.values.job?.estimatedRevenue;
                  const projectBilled =
                    accrualRevenue && estimatedRevenue
                      ? ((accrualRevenue / estimatedRevenue) * 100).toFixed(FIXED_VALUE)
                      : 0;
                  form.setFieldValue(`job.projectBilled`, projectBilled);
                }}
                thousandSeparator={true}
                prefix={'$'}
                disabled={!isEditing}
                decimalScale={2}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('financial.cashRevenue')}</InputLabel>
              <NumericFormat
                customInput={TextField}
                value={cast(form.values.job.cashRevenue)}
                onValueChange={(val) => {
                  form.setFieldValue(`job.cashRevenue`, val.floatValue);

                  // Update projectBilled, grossProfit, and grossProfitMargin
                  const cashRevenue = val.floatValue ?? 0;
                  const accrualRevenue = form.values.job.accrualRevenue ?? 0;
                  const costs = form.values.job.costs ?? 0;
                  const estimatedRevenue = form.values.job?.estimatedRevenue;
                  const projectBilled =
                    accrualRevenue && estimatedRevenue
                      ? ((accrualRevenue / estimatedRevenue) * 100).toFixed(FIXED_VALUE)
                      : 0;

                  const grossProfit = cashRevenue - costs || 0;
                  const grossProfitMargin = cashRevenue ? ((grossProfit / cashRevenue) * 100).toFixed(FIXED_VALUE) : 0;

                  form.setFieldValue(`job.projectBilled`, projectBilled);
                  form.setFieldValue(`job.grossProfit`, grossProfit);
                  form.setFieldValue(`job.grossProfitMargin`, grossProfitMargin);
                }}
                thousandSeparator={true}
                prefix={'$'}
                disabled={!isEditing}
                decimalScale={2}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('financial.projectBilled')}</InputLabel>
              <NumericFormat
                customInput={TextField}
                value={cast(form.values.job.projectBilled)}
                onValueChange={(val) => form.setFieldValue(`job.projectBilled`, val.floatValue)}
                thousandSeparator={true}
                suffix={'%'}
                disabled={!isEditing}
                decimalScale={2}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('financial.estimatedCosts')}</InputLabel>
              <NumericFormat
                customInput={TextField}
                value={cast(form.values.job.estimatedCosts)}
                onValueChange={(val) => {
                  const estimatedCosts = val.floatValue ?? 0;

                  form.setFieldValue(`job.estimatedCosts`, estimatedCosts);

                  const estimatedGrossProfit = (form.values.job?.estimatedRevenue ?? 0) - estimatedCosts;
                  form.setFieldValue(`job.estimatedGrossProfit`, estimatedGrossProfit);

                  const estimatedRevenue = form.values.job?.estimatedRevenue ?? 1;
                  const estimatedMargin = parseFloat(
                    ((estimatedGrossProfit / estimatedRevenue) * 100).toFixed(FIXED_VALUE)
                  );
                  form.setFieldValue(`job.estimatedMargin`, estimatedMargin);
                }}
                thousandSeparator={true}
                prefix={'$'}
                disabled={!isEditing}
                decimalScale={2}
              />
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[30px]'>
            <Box className='w-full'>
              <InputLabel>{t('financial.estimatedGrossProfit')}</InputLabel>
              <NumericFormat
                customInput={TextField}
                value={cast(form.values.job.estimatedGrossProfit)}
                onValueChange={(val) => form.setFieldValue(`job.estimatedGrossProfit`, val.floatValue)}
                thousandSeparator={true}
                prefix={'$'}
                disabled={!isEditing}
                decimalScale={2}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('financial.estimatedMargin')}</InputLabel>
              <NumericFormat
                customInput={TextField}
                value={cast(form.values.job.estimatedMargin)}
                onValueChange={(val) => form.setFieldValue(`job.estimatedMargin`, val.floatValue)}
                thousandSeparator={true}
                suffix={'%'}
                decimalScale={2}
                fixedDecimalScale={true}
                disabled={!isEditing}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('financial.costs')}</InputLabel>
              <NumericFormat
                customInput={TextField}
                value={cast(form.values.job.costs)}
                onValueChange={(val) => {
                  form.setFieldValue(`job.costs`, val.floatValue);

                  // Update grossProfit and grossProfitMargin
                  const costs = val.floatValue ?? 0;
                  const cashRevenue = form.values.job.cashRevenue ?? 0;
                  const grossProfit = cashRevenue - costs || 0;
                  const grossProfitMargin = cashRevenue ? ((grossProfit / cashRevenue) * 100).toFixed(FIXED_VALUE) : 0;

                  form.setFieldValue(`job.grossProfit`, grossProfit);
                  form.setFieldValue(`job.grossProfitMargin`, grossProfitMargin);
                }}
                thousandSeparator={true}
                prefix={'$'}
                disabled={!isEditing}
                decimalScale={2}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('financial.grossProfit')}</InputLabel>
              <NumericFormat
                customInput={TextField}
                value={cast(form.values.job.grossProfit)}
                onValueChange={(val) => form.setFieldValue(`job.grossProfit`, val.floatValue)}
                thousandSeparator={true}
                prefix={'$'}
                disabled={!isEditing}
                decimalScale={2}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('financial.grossProfitMargin')}</InputLabel>
              <NumericFormat
                customInput={TextField}
                value={cast(form.values.job.grossProfitMargin)}
                onValueChange={(val) => form.setFieldValue(`job.grossProfitMargin`, val.floatValue)}
                thousandSeparator={true}
                suffix={'%'}
                disabled={!isEditing}
                decimalScale={2}
              />
            </Box>
          </BaseRow>
        </CardContent>
        {permissions[PERMISSIONS.project_edit] && (
          <Box className='justify-end flex mb-[20px] mr-[20px]'>
            {isEditing ? (
              <>
                <Button className='text-[#758CA5] bg-[#F0F7FF] mr-[10px]' onClick={handleCancel}>
                  {t('cancel', { ns: 'admin/common' })}
                </Button>
                <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
                  {t('save', { ns: 'admin/common' })}
                </Button>
              </>
            ) : (
              <Button color='primary' variant='contained' onClick={handleEdit}>
                {t('edit', { ns: 'admin/common' })}
              </Button>
            )}
          </Box>
        )}
      </Card>

      <Card className='mb-[30px]'>
        <CardContent>
          <BaseRow className='mb-[30px]'>
            <Typography variant='h5'>{t('invoiceTitle')}</Typography>
            <Box className='ml-auto'>
              {permissions[PERMISSIONS.invoice_delete] && (
                <Button
                  className={cx(
                    'mr-[10px]',
                    { 'visibility: visible': selectedIds.length > 0 },
                    { 'visibility: hidden': selectedIds.length <= 0 }
                  )}
                  onClick={openAlertDeleteModal}
                  variant='contained'
                  color='error'
                  disableElevation
                  startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
                >
                  {t('delete')}
                </Button>
              )}
              {permissions[PERMISSIONS.invoice_create] && (
                <Button
                  variant='contained'
                  startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
                  onClick={() => handleOpenEditInvoice(true)}
                >
                  {t('addInvoice')}
                </Button>
              )}
            </Box>
          </BaseRow>
          <InvoiceList onOpenEdit={handleOpenEdit} editData={editData} />
          <EditInvoiceDialog
            open={openEditDialog}
            mode={editDialogMode}
            setOpen={handleOpenEditInvoice}
            editData={editData}
            data={props.data}
            updateData={props.updateData}
          />
        </CardContent>
      </Card>

      <Card className='mb-[30px]'>
        <CardContent>
          <BaseRow className='mb-[30px]'>
            <Typography variant='h5'>{t('cogs')}</Typography>
            <Box className='ml-auto'>
              {permissions[PERMISSIONS.cogs_delete] && (
                <Button
                  className={cx(
                    'mr-[10px]',
                    { 'visibility: visible': selectedIdsCogs.length > 0 },
                    { 'visibility: hidden': selectedIdsCogs.length <= 0 }
                  )}
                  onClick={openAlertDeleteModalCOGS}
                  variant='contained'
                  color='error'
                  disableElevation
                  startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
                >
                  {t('delete')}
                </Button>
              )}
              {permissions[PERMISSIONS.cogs_create] && (
                <Button
                  variant='contained'
                  startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
                  onClick={() => handleOpenEditCOGS(true)}
                >
                  {t('addCogs')}
                </Button>
              )}
            </Box>
          </BaseRow>
          <COGSList onOpenEdit={handleOpenEditDataCOGS} editData={editDataCogs} />
          <EditCOGSDialog
            open={openEditDialogCOGS}
            mode={editDialogMode}
            setOpen={handleOpenEditCOGS}
            data={editDataCogs}
            jobData={props.data}
            updateData={props.updateData}
          />
        </CardContent>
      </Card>
      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedJobs}
      />

      <ModalAlertDelete
        open={openAlertDeleteCogs}
        description={t('alertDescription', { ns: 'admin/cogs' })}
        onCancel={closeAlertDeleteModalCOGS}
        onConfirm={onDeleteSelectedCOGS}
      />
    </>
  );
};

export default memo(Financial);
